<template>
  <div class="agent-bar" :dir="language === 'he' ? 'rtl' : 'ltr'">
    <!-- לא מחובר: כפתור כניסה, ובלחיצה טופס התחברות -->
    <template v-if="!agent">
      <div class="agent-login-toggle" v-if="!loginOpen">
        <button class="agent-link" @click="loginOpen = true">{{ t('agentLogin') }}</button>
      </div>

      <form class="agent-card agent-login" v-else @submit.prevent="login">
        <h3>{{ t('agentLoginTitle') }}</h3>
        <p class="agent-hint">{{ t('agentLoginHint') }}</p>
        <input type="email" v-model.trim="username" :placeholder="t('email')" autocomplete="username" dir="ltr" required>
        <input type="password" v-model="password" :placeholder="t('password')" autocomplete="current-password" dir="ltr" required>
        <p class="agent-error" v-if="error">{{ error }}</p>
        <div class="agent-actions">
          <button type="submit" class="agent-btn" :disabled="busy">{{ busy ? t('loading') : t('login') }}</button>
          <button type="button" class="agent-link" @click="loginOpen = false; error = ''">{{ t('cancel') }}</button>
        </div>
      </form>
    </template>

    <!-- מחובר: הארנק -->
    <div class="agent-card" v-else>
      <div class="agent-head">
        <div>
          <div class="agent-name">{{ agent.name }}<span v-if="agent.store"> · {{ agent.store }}</span></div>
          <div class="agent-sub">{{ t('agentMode') }}</div>
        </div>
        <button class="agent-link" @click="logout">{{ t('logout') }}</button>
      </div>

      <div class="wallet-row">
        <div>
          <div class="wallet-label">{{ t('balance') }}</div>
          <div class="wallet-balance">{{ money(wallet.balance) }}</div>
        </div>
        <div class="agent-actions">
          <button class="agent-btn" @click="openTopup" v-if="!topupOpen">{{ t('topup') }}</button>
          <button class="agent-link" @click="historyOpen = !historyOpen">{{ historyOpen ? t('hideHistory') : t('history') }}</button>
        </div>
      </div>

      <p class="agent-success" v-if="notice">{{ notice }}</p>

      <!-- טעינה: בחירת סכום, ואז חלון התשלום של Cardcom -->
      <div class="topup" v-if="topupOpen">
        <template v-if="!topupUrl">
          <div class="topup-chips">
            <button v-for="a in quickAmounts" :key="a" type="button" class="chip" :class="{ active: Number(topupAmount) === a }" @click="topupAmount = a">{{ money(a) }}</button>
          </div>
          <form class="topup-form" @submit.prevent="startTopup">
            <input type="number" v-model="topupAmount" :min="topupMin" :max="topupMax" step="1" dir="ltr" :placeholder="t('amount')">
            <button type="submit" class="agent-btn" :disabled="busy">{{ busy ? t('loading') : t('toPayment') }}</button>
            <button type="button" class="agent-link" @click="closeTopup">{{ t('cancel') }}</button>
          </form>
          <p class="agent-hint">{{ t('topupRange').replace('{min}', topupMin).replace('{max}', topupMax) }}</p>
          <p class="agent-error" v-if="error">{{ error }}</p>
        </template>
        <template v-else>
          <iframe ref="topupFrame" :src="topupUrl"></iframe>
          <button type="button" class="agent-link" @click="closeTopup">{{ t('cancel') }}</button>
        </template>
      </div>

      <div class="history" v-if="historyOpen">
        <p class="agent-hint" v-if="!wallet.transactions.length">{{ t('noTransactions') }}</p>
        <table v-else>
          <thead>
            <tr><th>{{ t('date') }}</th><th>{{ t('description') }}</th><th>{{ t('amount') }}</th><th>{{ t('balanceAfter') }}</th></tr>
          </thead>
          <tbody>
            <tr v-for="tr in wallet.transactions" :key="tr.id">
              <td>{{ formatDate(tr.date) }}</td>
              <td>{{ tr.description || t('type_' + tr.type) }}</td>
              <td dir="ltr" :class="tr.amount < 0 ? 'neg' : 'pos'">{{ (tr.amount > 0 ? '+' : '') + money(tr.amount) }}</td>
              <td dir="ltr">{{ money(tr.balance_after) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
const TOKEN_KEY = 'rental_agent_token';

// localStorage יכול להיות חסום (גלישה פרטית וכו') - אז הסוכן פשוט יתחבר שוב בכל טעינה
function readToken() {
  try { return localStorage.getItem(TOKEN_KEY) || ''; } catch (e) { return ''; }
}
function writeToken(v) {
  try { v ? localStorage.setItem(TOKEN_KEY, v) : localStorage.removeItem(TOKEN_KEY); } catch (e) { /* ignore */ }
}

export default {
  name: 'RentalAgentBar',
  props: {
    language: { type: String, default: 'he' }
  },
  emits: ['agent-changed'],
  data() {
    return {
      token: '',
      agent: null,
      wallet: { balance: 0, transactions: [] },
      loginOpen: false,
      username: '',
      password: '',
      busy: false,
      error: '',
      notice: '',
      historyOpen: false,
      topupOpen: false,
      topupAmount: 200,
      topupUrl: '',
      topupPaymentId: 0,
      topupMin: 50,
      topupMax: 5000,
      quickAmounts: [100, 200, 500, 1000],
      translations: {
        he: {
          agentLogin: 'כניסת סוכנים',
          agentLoginTitle: 'כניסת סוכן',
          agentLoginHint: 'אותו אימייל וסיסמה של פורטל הסוכנים',
          email: 'אימייל',
          password: 'סיסמה',
          login: 'התחברות',
          loading: 'רגע...',
          cancel: 'ביטול',
          logout: 'התנתקות',
          agentMode: 'מחובר כסוכן',
          balance: 'יתרה בארנק',
          topup: 'טעינת ארנק',
          history: 'היסטוריית תנועות',
          hideHistory: 'הסתר היסטוריה',
          amount: 'סכום',
          toPayment: 'למעבר לתשלום',
          topupRange: 'סכום טעינה: ₪{min} עד ₪{max}',
          noTransactions: 'אין עדיין תנועות בארנק',
          date: 'תאריך',
          description: 'תיאור',
          balanceAfter: 'יתרה',
          type_topup: 'טעינת ארנק',
          type_charge: 'חיוב',
          type_refund: 'החזר',
          type_adjust: 'עדכון',
          errLogin: 'אימייל או סיסמה שגויים, או שהסוכן עדיין לא אושר',
          errAmount: 'יש לבחור סכום בין ₪{min} ל-₪{max}',
          errNetwork: 'שגיאת תקשורת, נסו שוב',
          errSession: 'החיבור פג, יש להתחבר מחדש',
          topupDone: 'הארנק נטען בהצלחה',
          topupProcessing: 'התשלום התקבל ומעובד. היתרה תתעדכן בעוד כמה רגעים'
        },
        en: {
          agentLogin: 'Agent login',
          agentLoginTitle: 'Agent login',
          agentLoginHint: 'Same email and password as the agents portal',
          email: 'Email',
          password: 'Password',
          login: 'Log in',
          loading: 'One moment...',
          cancel: 'Cancel',
          logout: 'Log out',
          agentMode: 'Logged in as agent',
          balance: 'Wallet balance',
          topup: 'Top up wallet',
          history: 'Transaction history',
          hideHistory: 'Hide history',
          amount: 'Amount',
          toPayment: 'Go to payment',
          topupRange: 'Top-up amount: ₪{min} to ₪{max}',
          noTransactions: 'No wallet transactions yet',
          date: 'Date',
          description: 'Description',
          balanceAfter: 'Balance',
          type_topup: 'Wallet top-up',
          type_charge: 'Charge',
          type_refund: 'Refund',
          type_adjust: 'Adjustment',
          errLogin: 'Wrong email or password, or the agent is not approved yet',
          errAmount: 'Choose an amount between ₪{min} and ₪{max}',
          errNetwork: 'Network error, please try again',
          errSession: 'Session expired, please log in again',
          topupDone: 'Wallet topped up successfully',
          topupProcessing: 'Payment received and processing. The balance will update in a few moments'
        }
      }
    };
  },
  created() {
    window.addEventListener('message', this.onPaymentMessage);
    this.token = readToken();
    if (this.token) this.refresh();
  },
  beforeUnmount() {
    window.removeEventListener('message', this.onPaymentMessage);
  },
  methods: {
    t(key) {
      const dict = this.translations[this.language] || this.translations.he;
      return dict[key] || key;
    },
    money(v) {
      const n = Number(v) || 0;
      return (n < 0 ? '-' : '') + '₪' + Math.abs(n).toLocaleString('he-IL', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
    },
    formatDate(s) {
      const d = new Date(String(s).replace(' ', 'T'));
      if (isNaN(d)) return s;
      return d.toLocaleDateString('he-IL') + ' ' + d.toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' });
    },
    // api_vue עוטף כל תשובה ב-{ data: ... }
    call(action, data, onOk) {
      this.api(
        { action, data: Object.assign({ agent_token: this.token }, data || {}) },
        (res) => onOk(res && res.data ? res.data : res),
        () => { this.busy = false; this.error = this.t('errNetwork'); }
      );
    },
    setAgent(agent, wallet) {
      this.agent = agent;
      if (wallet) this.wallet = wallet;
      this.$emit('agent-changed', agent ? { agent, token: this.token } : null);
    },
    clearSession(message) {
      writeToken('');
      this.token = '';
      this.closeTopup();
      this.historyOpen = false;
      this.setAgent(null);
      if (message) { this.loginOpen = true; this.error = message; }
    },
    login() {
      this.error = '';
      this.busy = true;
      this.call('rental_agent_login', { username: this.username, password: this.password }, (r) => {
        this.busy = false;
        if (!r || !r.success) { this.error = this.t('errLogin'); return; }
        this.token = r.token;
        writeToken(r.token);
        this.password = '';
        this.loginOpen = false;
        this.setAgent(r.agent, r.wallet);
      });
    },
    logout() {
      this.call('rental_agent_logout', {}, () => {});
      this.clearSession();
    },
    refresh() {
      this.call('rental_agent_me', {}, (r) => {
        if (!r || !r.success) {
          // טוקן ששמור מביקור קודם ופג - לא מציגים שגיאה, רק מחזירים לכפתור הכניסה
          this.clearSession(this.agent ? this.t('errSession') : '');
          return;
        }
        this.setAgent(r.agent, r.wallet);
      });
    },
    openTopup() {
      this.error = '';
      this.notice = '';
      this.topupOpen = true;
    },
    closeTopup() {
      this.topupOpen = false;
      this.topupUrl = '';
      this.topupPaymentId = 0;
      this.busy = false;
    },
    startTopup() {
      const amount = Number(this.topupAmount);
      const rangeErr = this.t('errAmount').replace('{min}', this.topupMin).replace('{max}', this.topupMax);
      this.error = '';
      if (!(amount >= this.topupMin && amount <= this.topupMax)) { this.error = rangeErr; return; }

      this.busy = true;
      this.call('rental_agent_topup', { amount }, (r) => {
        this.busy = false;
        if (r && r.success && r.payment_link) {
          this.topupPaymentId = r.payment_id;
          this.topupUrl = r.payment_link;
        } else if (r && r.error === 'not_logged_in') {
          this.clearSession(this.t('errSession'));
        } else if (r && r.error === 'invalid_amount') {
          this.error = rangeErr;
        } else {
          this.error = this.t('errNetwork');
        }
      });
    },
    // payments/wallet_topup_success, נטען בתוך חלון התשלום אחרי החיוב
    onPaymentMessage(e) {
      const frame = this.$refs.topupFrame;
      const d = e.data;

      if (!frame || e.source !== frame.contentWindow) return;
      if (!d || d.source !== 'bestphone_wallet' || Number(d.payment_id) !== Number(this.topupPaymentId)) return;

      this.closeTopup();
      this.notice = d.status === 'paid' ? this.t('topupDone') : this.t('topupProcessing');
      this.refresh();
    }
  }
};
</script>

<style scoped>
.agent-bar {
  margin-bottom: 18px;
}

.agent-login-toggle {
  text-align: center;
}

.agent-card {
  background: #fff;
  border: 1.5px solid #ede8f5;
  border-radius: 14px;
  padding: 16px 18px;
  box-shadow: 0 4px 18px rgba(132, 36, 227, 0.08);
  color: #1a1a2e;
}

.agent-login {
  max-width: 380px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.agent-login h3 {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 800;
}

.agent-card input {
  padding: 10px 12px;
  border: 1.5px solid #e5e0f0;
  border-radius: 10px;
  font-size: 1rem;
  min-width: 0;
}

.agent-card input:focus {
  outline: none;
  border-color: #8424e3;
}

.agent-head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
}

.agent-name {
  font-weight: 800;
}

.agent-sub, .wallet-label, .agent-hint {
  font-size: 0.85rem;
  color: #6b6580;
  margin: 0;
}

.wallet-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 14px;
}

.wallet-balance {
  font-size: 1.8rem;
  font-weight: 800;
  color: #8424e3;
  direction: ltr;
  unicode-bidi: isolate;
}

.agent-actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
}

.agent-btn {
  background: linear-gradient(135deg, #8424e3 0%, #9b4de8 100%);
  color: #fff;
  border: none;
  border-radius: 10px;
  padding: 10px 20px;
  font-size: 0.95rem;
  font-weight: 800;
  cursor: pointer;
}

.agent-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.agent-link {
  background: none;
  border: none;
  color: #8424e3;
  font-weight: 700;
  font-size: 0.95rem;
  cursor: pointer;
  text-decoration: underline;
  padding: 6px 4px;
}

.agent-error {
  color: #dc2626;
  font-size: 0.9rem;
  margin: 0;
}

.agent-success {
  color: #15803d;
  font-weight: 700;
  margin: 12px 0 0;
}

.topup {
  margin-top: 14px;
  padding-top: 14px;
  border-top: 1px solid #ede8f5;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.topup-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.chip {
  padding: 7px 14px;
  border-radius: 20px;
  border: 1.5px solid #e5e0f0;
  background: #fafafe;
  font-weight: 700;
  cursor: pointer;
}

.chip.active {
  border-color: #8424e3;
  background: #faf5ff;
  color: #8424e3;
}

.topup-form {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
}

.topup-form input {
  width: 140px;
}

.topup iframe {
  width: 100%;
  height: 560px;
  border: 1.5px solid #ede8f5;
  border-radius: 12px;
}

.history {
  margin-top: 14px;
  overflow-x: auto;
}

.history table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.9rem;
}

.history th, .history td {
  padding: 8px 6px;
  border-bottom: 1px solid #ede8f5;
  text-align: start;
  white-space: nowrap;
}

.history th {
  color: #6b6580;
  font-weight: 700;
}

.history .pos { color: #15803d; }
.history .neg { color: #dc2626; }
</style>
